import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { RecordData, RequestError, Service } from 'webapp-types';
import { DefaultTeamColor } from 'webapp/const/board-color-config';
import { ModelName, TeamMemberStatus, TeamType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import TeamsPopupEditComponent from 'webapp/components/teams/popup-edit';
import TeamsPopupManageMembersComponent from 'webapp/components/teams/popup-manage-members';

import Team from 'webapp/models-custom/team';
import type TeamRecord from 'webapp/models/team';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export enum TeamsPusherEvents {
	UPDATED = 'TeamController.updateTeam',
	UPDATED_TEAM_MEMBERS = 'TeamController.updateTeamMembers',
}

export default class ModelManagerTeamsService extends ModelManagerForStoreService<
	TeamRecord,
	Team
> {
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('store') declare storeService: Service.Store;
	@service('constants') declare constantsService: Service.Constants;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;

	private _teamModels: Team[] = [];
	@cached
	get teamModels(): Team[] {
		this.updateModelCache(this._teamModels, this.savedRecords);
		return this._teamModels;
	}

	get userTeamModels(): Team[] {
		return this.teamModels.filter(
			(team) => !(team.type === TeamType.PRIVATE && !team.isUserMember),
		);
	}

	get randomColor(): string {
		const hexColors = this.constantsService.boardColors
			.filter((item) => !item.isLight)
			.map((item) => item.hex);
		return hexColors[Math.floor(Math.random() * hexColors.length)]!;
	}

	@cached
	get currentUserId(): number | null {
		return this.accountService.currentUserService.numericId;
	}

	// ------ Requests ------

	_teamsRequested = false;
	_teamsRequestPromise: Promise<void> | null = null;
	requestTeams(): Promise<void> {
		if (this.currentUserService.isViewer) {
			return Promise.resolve();
		}

		const variablePrefix = 'teams';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.TEAM);
			},
		);
	}

	createNewTeam(name: string): Team {
		const team = this.createNewRecord({
			name,
			color: DefaultTeamColor,
			type: TeamType.OPEN,
			ownerId: this.currentUserId,
		});

		return this.createModel(team);
	}

	openDeletePopup(team: Team, navigateToTeamsPage = false): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Deleting the space ...');

			this.analyticsService.event(TrackingEvent.TEAM_DELETED, {
				type: team.teamTypeForTracking,
			});

			team.delete()
				.then(
					() => {
						const isLastTeam = !this.userTeamModels.length;

						if (navigateToTeamsPage) {
							this.navigationService.transitionToRoute('teams');
						} else if (isLastTeam) {
							this.navigationService.transitionToRoute('index');
						}

						layer.ok('The space has been successfully deleted');
					},
					(errorData: RequestError) => {
						layer.close();
						throw errorData;
					},
				)
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Delete the "${team.name}" space?`,
			text: 'This will remove the space. It cannot be undone.',
			confirmAction: 'Delete space',
			cancelAction: 'Cancel',
			confirmWord: 'DELETE',
		});
	}

	requestToJoin(team: Team): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const { id, name } = team;

		const layer = this.infolayerService.loading(
			`Sending your request to join the ${name} space ...`,
		);

		const payload = {
			teamJoinRequest: {
				teamId: Number(id),
			},
		};

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
			type: 'requested',
		});

		this.requestService
			.api<RecordData.Payload>('POST', '/teams/join-requests', payload)
			.then(
				(payload) => {
					this.storeService.pushPayload(payload);
					layer.ok(`Request to join the ${name} space was sent to the space owner.`);
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	joinTeam(team: Team): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const { id } = team;
		const layer = this.infolayerService.loading('Joining the space ...');

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
			type: 'joined',
		});

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
			type: 'joined',
		});

		this.requestService
			.api<RecordData.Payload>('POST', `/teams/${id}/join`)
			.then(
				(payload) => {
					layer.ok('You have successfully joined the team');
					this.storeService.pushPayload(payload);
					this.navigationService.replaceRoute(`/teams/${id}`);
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	openEditPopup(team: Team) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.createModelFromRecord(team.model),
			popup = this.popupService.createModal(TeamsPopupEditComponent, {
				popupClass: 'popup-type-teams-edit',
				allowStacked: true,
			});

		popup.open({
			model,
		});
	}

	createAndOpenEditTeamPopup(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const ownerId = this.accountService.currentUserService.numericId!;
		const teamRecord = this.createNewRecord({
			type: TeamType.OPEN,
			ownerId,
			color: this.randomColor,
			teamMembers: [{ id: ownerId, status: TeamMemberStatus.MEMBER }],
		});

		const model = this.createModel(teamRecord);
		this.openEditPopup(model);
	}

	handleLeave(team: Team) {
		if (team.isOwnedByMe) {
			const hasMultiple = (team.teamMembers?.length ?? 0) > 1;

			if (!team.isOpen && hasMultiple) {
				this.openChangeOwnerPopup(team);
			} else {
				this.openLeaveTeamPopup(team);
			}
		} else {
			this.openLeaveTeamPopup(team);
		}
	}

	handleDelete(team: Team) {
		const hasMultiple = (team.teamMembers?.length ?? 0) > 1;
		if (!hasMultiple) {
			this.openLeaveTeamAndDeletePopup(team);
		} else {
			this.openDeletePopup(team, true);
		}
	}

	leaveTeam(team: Team) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const layer = this.infolayerService.loading('Leaving the space ...');

		this.analyticsService.event(TrackingEvent.TEAM_MEMBER_CHANGED, {
			type: 'left',
		});

		return this.requestService
			.api<RecordData.Payload>('DELETE', `/teams/${team.id}/join`)
			.then(
				(data) => {
					this.storeService.pushPayload(data);
					this.navigationService.replaceRoute(`/teams`);
					layer.ok('You have successfully left the team');
				},
				(errorData: RequestError) => {
					layer.close();
					throw errorData;
				},
			)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	openLeaveTeamPopup(team: Team) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.popupService.openConfirmPopup({
			onConfirm: () => {
				void this.leaveTeam(team);
			},
			title: `Leave the "${team.name}" space?`,
			text: 'You`ll lose access to all space-related content.',
			confirmAction: 'Leave space',
			cancelAction: 'Cancel',
		});
	}

	openLeaveTeamAndDeletePopup(team: Team) {
		if (this.accountService.handleDemo()) {
			return;
		}

		const affirmativeFunc = () => {
			const layer = this.infolayerService.loading('Leaving the space as the last member ...');

			this.analyticsService.event(TrackingEvent.TEAM_DELETED, {
				type: team.teamTypeForTracking,
			});

			team.delete()
				.then(() => {
					layer.ok('The space has been successfully deleted');
					this.navigationService.replaceRoute('/teams');
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: `Leave the "${team.name}" space?`,
			text: 'Since you’re the only member left, leaving the space will delete it permanently. <strong> This cannot be undone. </strong>',
			confirmAction: 'Leave & delete space',
			cancelAction: 'Cancel',
			confirmWord: 'DELETE',
		});
	}

	openChangeOwnerPopup(model: Team): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmUsersService.openUserSelectPopup({
			title: `Assign a new owner to "${model.name}" space before leaving.`,
			text: 'You`ll lose access to all space-related content. Please select a different user to be the new space owner.',
			currentUserId: model.stringifiedOwnerId,
			onSelect: (userId: string | null) => {
				model.ownerId = userId ? +userId : null;
				model
					.save()
					.then(() => {
						void this.leaveTeam(model);
					})
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			},
		});
	}

	openManageTeamMembersPopup(team: Team): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.createModelFromRecord(team.model),
			popup = this.popupService.createModal(TeamsPopupManageMembersComponent, {
				allowStacked: true,
				popupClass: 'popup-type-teams-manage-members',
			});

		popup.open({
			model,
		});
	}

	requestSections(teamId: string) {
		return this.requestService
			.api<RecordData.Payload>('GET', `/teams/${teamId}/sections`)
			.then((data) => {
				this.storeService.pushPayload(data);
				return data;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	requestData(teamId: string) {
		return this.requestService
			.api<RecordData.Payload>('GET', `/teams/${teamId}/data`)
			.then((data) => {
				this.storeService.pushPayload(data);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	onTeamUpdate(teams: RecordData.Team.Raw[]) {
		if (this.navigationService.activatedRoute === 'teams.view') {
			const userId = this.currentUserId;

			const hasCurrentUserBeenRemoved = teams.some(
				(team) => !team.teamMembers.some((member) => member.id === userId),
			);

			if (hasCurrentUserBeenRemoved) {
				this.navigationService.transitionToRoute('teams');
			}
		}
	}

	// ------ For-store ------

	modelName = ModelName.TEAM;

	createModel(model: TeamRecord): Team {
		return new Team({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/teams': ModelManagerTeamsService;
	}
}
